import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { roundToFixed } from '../helpers/math';
import styles from './MoldContainer.module.scss';
import useLocalize from '../hooks/use-localize';

const AREA_UNIT = 'cm²';
const VOLUME_UNIT = 'cm³';

const renderMeasure = (label, mold, totalValue, unitValue, unit) => (
  <>
    <div className={styles.detailLabel}>{label}</div>
    <div className={styles.detailValue}>
      {unitValue ? `(${roundToFixed(unitValue, 1)} ${unit} / ${mold}) ` : null}
      {roundToFixed(totalValue, 1)}&nbsp;{unit}
    </div>
  </>
);

const renderArea = (label, mold, totalArea, unitArea) => renderMeasure(label, mold, totalArea, unitArea, AREA_UNIT);

const renderVolume = (label, mold, volume, unitVolume) => renderMeasure(label, mold, volume, unitVolume, VOLUME_UNIT);

const DetailedCalculations = ({ area, areDetailsVisible, crustArea, crustUnitArea, crustUnitVolume, crustVolume, isAreaEnabled, isCrustEnabled, moldCount, unitArea, unitVolume, volume }) => {
  const { localize } = useLocalize();

  const moldLocalizedStr = useMemo(() => localize('mold.calculations.mold'), [localize]);

  const crustElt = useMemo(() => {
    if (!isCrustEnabled || crustArea === -1) {
      return null;
    }

    return (
      <>
        {renderArea(localize('mold.calculations.dough_area'), moldLocalizedStr, crustArea, moldCount > 1 ? crustUnitArea : undefined)}
        {renderVolume(localize('mold.calculations.dough_volume'), moldLocalizedStr, crustVolume, moldCount > 1 ? crustUnitVolume : undefined)}
      </>
    );
  }, [crustArea, crustUnitArea, crustUnitVolume, crustVolume, isCrustEnabled, moldCount]);

  if (!areDetailsVisible) {
    return null;
  }

  return (
    <div className={styles.grid}>
      <div className={styles.title}>
        <span aria-label='square emoji' role='img'>
          📐
        </span>
        {localize('mold.calculations.title')}
      </div>
      {crustElt}
      {isAreaEnabled ? renderArea(localize('mold.calculations.mold_base_area'), moldLocalizedStr, area, moldCount > 1 ? unitArea : undefined) : null}
      {renderVolume(localize('mold.calculations.mold_volume'), moldLocalizedStr, volume, moldCount > 1 ? unitVolume : undefined)}
    </div>
  );
};

DetailedCalculations.propTypes = {
  area: PropTypes.number.isRequired,
  areDetailsVisible: PropTypes.bool.isRequired,
  crustArea: PropTypes.number.isRequired,
  crustUnitArea: PropTypes.number.isRequired,
  crustUnitVolume: PropTypes.number.isRequired,
  crustVolume: PropTypes.number.isRequired,
  isAreaEnabled: PropTypes.bool.isRequired,
  isCrustEnabled: PropTypes.bool.isRequired,
  moldCount: PropTypes.number.isRequired,
  unitArea: PropTypes.number.isRequired,
  unitVolume: PropTypes.number.isRequired,
  volume: PropTypes.number.isRequired,
};

export default memo(DetailedCalculations);
